import { CSSProperties, useCallback, useMemo } from "react";
import cn from "classnames";
import { FCX, ID } from "@datassential/platform-ui-lib";
import { getSvgPath, Point } from "@components/SocialLineChart/SocialLineChart.helpers";
import { ChartOption, Series } from "./GroupsColumnsLineChart";

const AXIS_LABEL_HEIGHT = 16;

const Curves: FCX<{
    series: Series[];
    groups: ChartOption[];
    columns: ChartOption[];
    labels: number[];
    height: number;
    width: number;
    groupGap: number;
    activeSeriesId: ID;
    activeGroupId: ID;
    activeColumnId: ID;
}> = ({
    series,
    groups,
    columns,
    activeGroupId,
    activeColumnId,
    labels,
    height,
    width,
    groupGap,
    activeSeriesId,
}) => {
    const minValue = useMemo((): number => labels[labels.length - 1], [labels]);
    const maxValue = useMemo((): number => labels[0], [labels]);

    const groupWidth = useMemo(
        () => (width - groupGap * Math.max(0, groups.length - 1)) / Math.max(1, groups.length),
        [width, groupGap, groups.length],
    );
    const columnWidth = useMemo(
        () => groupWidth / Math.max(1, columns.length),
        [groupWidth, columns.length],
    );

    const getX = useCallback(
        (groupIndex: number, columnIndex: number): number =>
            groupIndex * (groupWidth + groupGap) + (columnIndex + 0.5) * columnWidth,
        [groupWidth, columnWidth, groupGap],
    );
    const getY = useCallback(
        (value: number): number => {
            if (maxValue === minValue) return height / 2;
            const inset = AXIS_LABEL_HEIGHT / 2;
            return inset + (height - AXIS_LABEL_HEIGHT) * (1 - (value - minValue) / (maxValue - minValue));
        },
        [height, maxValue, minValue],
    );

    const seriesCurves = useMemo(
        () => series.map((s, index) => {
            const points: Array<Point & { groupId: ID; columnId: ID }> = [];

            groups.forEach((g, groupIndex) => {
                columns.forEach((c, columnIndex) => {
                    const value = s.values.find(v => v.groupId === g.id && v.columnId === c.id);

                    if (value && value.value !== null) {
                        points.push({
                            x: getX(groupIndex, columnIndex),
                            y: getY(value.value),
                            groupId: g.id,
                            columnId: c.id,
                        });
                    }
                });
            });

            const animationMs = 30 * groups.length * columns.length;

            return (
                <g
                    key={`${s.id}`}
                    className={cn("GroupsColumnsLineChart__curve-container", {
                        "is-dimmed": activeSeriesId !== null && activeSeriesId !== s.id,
                    })}
                >
                    <path
                        fill="none"
                        pathLength={1}
                        style={{
                            zIndex: activeSeriesId === s.id ? 2 : undefined,
                            "--animation-duration": `${animationMs}ms`,
                        } as CSSProperties}
                        stroke={s.color}
                        d={getSvgPath(points)}
                        className="GroupsColumnsLineChart__curve"
                        data-testid={`blockLineChartCurve_${index}`}
                    />
                    {points.map((point, pIndex) => (
                        <circle
                            key={`${s.id}-${point.groupId}-${point.columnId}`}
                            cx={point.x}
                            cy={point.y}
                            style={{ color: s.color, animationDelay: `${animationMs}ms` }}
                            stroke={s.color}
                            className={cn("GroupsColumnsLineChart__dot", "appears", "fast", {
                                "is-active": point.groupId === activeGroupId && point.columnId === activeColumnId,
                            })}
                            data-testid={`blockLineChartDot_${index}_${pIndex}`}
                        />
                    ))}
                </g>
            );
        }),
        [series, groups, columns, getX, getY, activeSeriesId, activeGroupId, activeColumnId],
    );

    return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
            {seriesCurves}
        </svg>
    );
};

export default Curves;
